import React, { useState, useEffect } from 'react';
import { 
  Box, Button, Typography, Paper, Alert, Snackbar, CircularProgress, TextField, Stack,
  Grid, Card, CardContent, CardActions, FormControl, InputLabel, Select, MenuItem, 
  Checkbox, ListItemText, OutlinedInput, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions,
  TablePagination
} from '@mui/material';
import { Info as InfoIcon, Download as DownloadIcon, Lightbulb as LightbulbIcon } from '@mui/icons-material';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../apiBase';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function Datacenter() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [step, setStep] = useState(1); // 1: Send Email, 2: Verify OTP, 3: Dashboard, 4: Admin Data Management
  const [loginId, setLoginId] = useState(null);
  const [otp, setOtp] = useState('');
  
  // Dashboard states
  const [tables, setTables] = useState([]);
  const [selectedTables, setSelectedTables] = useState([]); // Array of strings (table names)
  const [restoreFile, setRestoreFile] = useState(null);

  // Step 4 states
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [downloadHistory, setDownloadHistory] = useState([]);
  
  // Preview states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [previewTable, setPreviewTable] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Download OTP states
  const [downloadOtpOpen, setDownloadOtpOpen] = useState(false);
  const [downloadOtpStep, setDownloadOtpStep] = useState(1); // 1: Send, 2: Verify
  const [downloadOtp, setDownloadOtp] = useState('');
  const [downloadLoginId, setDownloadLoginId] = useState(null);
  const [pendingDownloadType, setPendingDownloadType] = useState(null);

  // Download Link Modal states
  const [downloadLinkOpen, setDownloadLinkOpen] = useState(false);
  const [downloadLinkUrl, setDownloadLinkUrl] = useState('');

  useEffect(() => {
    if (step === 3 || step === 4) {
      fetchTables();
    }
    if (step === 4) {
      fetchHistory();
    }
  }, [step]);

  const fetchTables = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/datacenter/tables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setTables(data.tables || []);
      } else {
        setResult({ type: 'error', message: 'Failed to fetch tables' });
      }
    } catch (error) {
      console.error(error);
      setResult({ type: 'error', message: 'Error fetching tables' });
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/datacenter/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setDownloadHistory(data.history || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getDisplayName = (tableName) => {
    const map = {
      'dailytransactionheadofficemanul': 'Daily Transaction Manual',
      'dailyvendorfollowup': 'Daily Vendor Followup',
      'hardcopy_sheets': 'Hard Copysheet'
    };
    return map[tableName] || tableName;
  };

  const handleSendEmail = async () => {
    setLoading(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/datacenter/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      const data = await res.json();
      if (res.ok) {
        setResult({ type: 'success', message: data.message || 'OTP sent successfully!' });
        if (data.loginId) {
          setLoginId(data.loginId);
          setStep(2);
        }
      } else {
        setResult({ type: 'error', message: data.message || 'Failed to send OTP' });
      }
    } catch (err) {
      setResult({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setLoading(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/datacenter/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ loginId, otp }),
      });

      const data = await res.json();
      if (res.ok) {
        setResult({ type: 'success', message: data.message || 'OTP Verified Successfully!' });
        setOtp('');
        setStep(3); // Go to Dashboard
      } else {
        setResult({ type: 'error', message: data.message || 'OTP Verification Failed' });
      }
    } catch (err) {
      setResult({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleLockDatacenter = () => {
    setStep(1);
    setLoginId(null);
    setSelectedTables([]);
    setRestoreFile(null);
    setResult({ type: 'info', message: 'Datacenter locked.' });
  };

  const handleTableChange = (event) => {
    const {
      target: { value },
    } = event;
    
    // Check if value is array of strings (names) or objects
    // The Select component will return array of strings because value prop is array of strings
    
    if (value.includes('All Tables')) {
        if (selectedTables.length === tables.length) {
            setSelectedTables([]);
        } else {
            setSelectedTables(tables.map(t => t.name));
        }
        return;
    }

    setSelectedTables(
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const toggleTableSelection = (tableName) => {
    const currentIndex = selectedTables.indexOf(tableName);
    const newSelected = [...selectedTables];

    if (currentIndex === -1) {
      newSelected.push(tableName);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    setSelectedTables(newSelected);
  };

  const handleSelectAllTables = (event) => {
    if (event.target.checked) {
      setSelectedTables(tables.map(t => t.name));
    } else {
      setSelectedTables([]);
    }
  };

  const handleBackup = async () => {
      // Placeholder for Step 3
      alert(`Backing up: ${selectedTables.length > 0 ? selectedTables.join(', ') : 'All Tables'}`);
  };

  const handleDownloadZipStep3 = async () => {
      // Transition to Step 4
      setStep(4);
  };

  const handleRestore = async () => {
      if (!restoreFile) return;
      alert(`Restoring from: ${restoreFile.name}`);
  };

  const handleDownloadAction = async (type) => {
    // Open OTP Modal instead of direct download
    setPendingDownloadType(type);
    setDownloadOtpStep(1);
    setDownloadOtp('');
    setDownloadLoginId(null);
    setDownloadOtpOpen(true);
  };

  const handleSendDownloadOtp = async () => {
    setLoading(true);
    try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/datacenter/send-download-otp`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({}),
        });

        const data = await res.json();
        if (data.success) {
            setDownloadLoginId(data.loginId);
            setDownloadOtpStep(2);
            setResult({ type: 'success', message: 'OTP sent to your email!' });
        } else {
            setResult({ type: 'error', message: data.message || 'Failed to send OTP' });
        }
    } catch (err) {
        setResult({ type: 'error', message: err.message });
    } finally {
        setLoading(false);
    }
  };

  const handleVerifyAndDownload = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/datacenter/download-zip`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
                tables: selectedTables,
                dateFrom,
                dateTo,
                type: pendingDownloadType,
                loginId: downloadLoginId,
                otp: downloadOtp
            }),
        });
        const data = await res.json();
        if (data.success) {
            setResult({ type: 'success', message: 'Download prepared successfully!' });
            if (data.downloadUrl) {
                setDownloadLinkUrl(data.downloadUrl);
                setDownloadLinkOpen(true);
            }
            fetchHistory(); // Refresh history
            handleCloseDownloadOtp();
        } else {
            setResult({ type: 'error', message: data.message });
        }
      } catch (err) {
          setResult({ type: 'error', message: err.message });
      } finally {
          setLoading(false);
      }
  };

  const handleCloseDownloadOtp = () => {
    setDownloadOtpOpen(false);
    setDownloadOtpStep(1);
    setDownloadOtp('');
    setDownloadLoginId(null);
    setPendingDownloadType(null);
  };

  const handlePreview = async (tableName, newPage = 0, newLimit = 10) => {
    setPreviewTable(tableName);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData([]);
    setPage(newPage);
    setRowsPerPage(newLimit);

    try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/datacenter/preview/${tableName}?page=${newPage + 1}&limit=${newLimit}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
            setPreviewData(data.rows || []);
            if (data.pagination) {
                setTotalRecords(data.pagination.totalRecords);
            }
        } else {
            setResult({ type: 'error', message: data.message });
        }
    } catch (err) {
        setResult({ type: 'error', message: 'Failed to fetch preview' });
    } finally {
        setPreviewLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    handlePreview(previewTable, newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    handlePreview(previewTable, 0, newLimit);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewTable('');
    setPreviewData([]);
    setPage(0);
    setRowsPerPage(10);
  };

  const formatCellValue = (key, value) => {
    if (!value) return '';
    if (key.toLowerCase().includes('date') || key === 'created_at' || key === 'updated_at') {
        return new Date(value).toLocaleDateString('en-GB'); // DD/MM/YYYY
    }
    return String(value);
  };

  const renderStep4 = () => (
    <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                <span role="img" aria-label="db">🗄️</span> Admin Data Management
            </Typography>
        </Box>

        <Paper variant="outlined" sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={selectedTables.length === tables.length && tables.length > 0}
                        indeterminate={selectedTables.length > 0 && selectedTables.length < tables.length}
                        onChange={handleSelectAllTables}
                    />
                }
                label={<Typography variant="h6">Select All Tables</Typography>}
            />
            <Typography variant="body1" color="primary" fontWeight="bold">
                {selectedTables.length} tables selected
            </Typography>
        </Paper>

        <Grid container spacing={2} sx={{ mb: 4 }}>
            {tables.map((table) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={table.name}>
                    <Card variant="outlined" sx={{ position: 'relative' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                    <span role="img" aria-label="table" style={{ marginRight: 8, flexShrink: 0 }}>📅</span>
                                    <Typography variant="h6" noWrap title={getDisplayName(table.name)}>
                                        {getDisplayName(table.name)}
                                    </Typography>
                                </Box>
                                <Checkbox 
                                    checked={selectedTables.indexOf(table.name) > -1}
                                    onChange={() => toggleTableSelection(table.name)}
                                    sx={{ p: 0, ml: 1 }}
                                />
                            </Box>
                            <Stack spacing={0.5}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <span role="img" aria-label="list" style={{ marginRight: 8, fontSize: 12 }}>list</span>
                                    <Typography variant="body2" fontWeight="bold">{table.rows} records</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <span role="img" aria-label="grid" style={{ marginRight: 8, fontSize: 12 }}>grid_on</span>
                                    <Typography variant="body2">{table.cols} columns</Typography>
                                </Box>
                            </Stack>
                            <Button variant="outlined" size="small" sx={{ mt: 2 }} onClick={() => handlePreview(table.name)}>
                                <span role="img" aria-label="eye" style={{ marginRight: 4 }}>👁️</span> Preview
                            </Button>
                        </CardContent>
                    </Card>
                </Grid>
            ))}
        </Grid>

        <Paper sx={{ p: 3, bgcolor: '#e3f2fd', mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <span role="img" aria-label="download" style={{ fontSize: 24, marginRight: 8 }}>📥</span>
                <Typography variant="h5">Download Selected Data</Typography>
            </Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
                Select tables and choose your preferred download format. Both options require OTP verification.
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
                Note: When multiple tables are selected, the "Individual CSV Download" option will create a ZIP file containing separate CSV files for each table.
            </Alert>

            <Paper sx={{ p: 2, bgcolor: '#1976d2', color: 'white', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <span role="img" aria-label="calendar" style={{ marginRight: 8 }}>📅</span>
                    <Typography variant="h6">Select Date Range (Optional)</Typography>
                </Box>
                <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 5 }}>
                        <Typography variant="caption" display="block" sx={{ color: 'white', mb: 0.5 }}>From Date:</Typography>
                        <TextField
                            type="date"
                            fullWidth
                            size="small"
                            sx={{ bgcolor: 'white', borderRadius: 1 }}
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                        />
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Leave empty to include all data from beginning</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 5 }}>
                        <Typography variant="caption" display="block" sx={{ color: 'white', mb: 0.5 }}>To Date:</Typography>
                        <TextField
                            type="date"
                            fullWidth
                            size="small"
                            sx={{ bgcolor: 'white', borderRadius: 1 }}
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                        />
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>Leave empty to include all data until today</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 2 }} sx={{ textAlign: 'right' }}>
                         <Button 
                            variant="contained" 
                            color="inherit" 
                            sx={{ color: '#333', bgcolor: '#e0e0e0' }}
                            onClick={() => { setDateFrom(''); setDateTo(''); }}
                        >
                             <span role="img" aria-label="close" style={{ marginRight: 4 }}>✖</span> Clear
                         </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                        <Box sx={{ mb: 2 }}>
                            <span role="img" aria-label="folder" style={{ fontSize: 24, marginRight: 8 }}>📂</span>
                            <Typography variant="h6" component="span">Choose Save Location (ZIP)</Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Get download link to choose where to save ZIP file
                        </Typography>
                        <Button 
                            variant="contained" 
                            color="warning" 
                            size="large" 
                            fullWidth
                            onClick={() => handleDownloadAction('ZIP')}
                            disabled={loading}
                        >
                            <span role="img" aria-label="folder-open" style={{ marginRight: 8 }}>📂</span> Get ZIP Download Link
                        </Button>
                        <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                            ⓘ Right-click the download link and select "Save link as..." to choose your folder
                        </Typography>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                         <Box sx={{ mb: 2 }}>
                            <span role="img" aria-label="folder" style={{ fontSize: 24, marginRight: 8 }}>📂</span>
                            <Typography variant="h6" component="span">Choose Save Location (CSV)</Typography>
                        </Box>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Get download link to choose where to save CSV files
                        </Typography>
                        <Button 
                            variant="contained" 
                            color="info" 
                            size="large" 
                            fullWidth
                            onClick={() => handleDownloadAction('CSV')}
                            disabled={loading}
                        >
                             <span role="img" aria-label="folder-open" style={{ marginRight: 8 }}>📂</span> Get CSV Download Link
                        </Button>
                         <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                            ⓘ Right-click the download link and select "Save link as..." to choose your folder
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>
        </Paper>

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: '#546e7a', color: 'white', display: 'flex', alignItems: 'center' }}>
                <span role="img" aria-label="clock" style={{ marginRight: 8 }}>🕒</span>
                <Typography variant="h6">Download History</Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 440 }}>
                <Table stickyHeader aria-label="sticky table">
                <TableHead>
                    <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Downloaded At</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Tables</TableCell>
                    <TableCell>Date Range</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>File Name</TableCell>
                    <TableCell>Records</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {downloadHistory.map((row, index) => (
                    <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{new Date(row.created_at).toLocaleString()}</TableCell>
                        <TableCell>{row.user_id}</TableCell>
                        <TableCell>{row.tables_count === 0 ? 'All' : `${row.tables_count} tables`}</TableCell>
                        <TableCell>
                            <Chip label={row.date_range} size="small" color="default" sx={{ bgcolor: '#757575', color: 'white' }} />
                        </TableCell>
                        <TableCell>
                            <Chip label={row.file_type} color="primary" size="small" />
                        </TableCell>
                        <TableCell>{row.file_name}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>{row.record_count}</TableCell>
                    </TableRow>
                    ))}
                    {downloadHistory.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={8} align="center">No download history found</TableCell>
                        </TableRow>
                    )}
                </TableBody>
                </Table>
            </TableContainer>
        </Paper>

    </Box>
  );

  return (
    <Layout title="Datacenter">
      {step === 1 && (
        <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              Datacenter Operations
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              Click the button below to generate an OTP and send it to your registered email.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSendEmail}
              disabled={loading}
              sx={{ minWidth: 200 }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
            </Button>
          </Paper>
        </Box>
      )}

      {step === 2 && (
        <Box sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              Datacenter Operations
            </Typography>
            <Stack spacing={3} alignItems="center">
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                  Enter the OTP sent to your email.
                </Typography>
                <TextField
                  label="Enter OTP"
                  variant="outlined"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  fullWidth
                  sx={{ maxWidth: 300 }}
                />
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  onClick={handleVerifyOtp}
                  disabled={loading || !otp}
                  sx={{ minWidth: 200 }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
                </Button>
                <Button variant="text" onClick={() => setStep(1)} disabled={loading}>
                  Back / Resend
                </Button>
            </Stack>
          </Paper>
        </Box>
      )}

      {step === 3 && (
        <Box sx={{ p: 3 }}>
           <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Typography variant="h4" component="h1">
                  Data Center
              </Typography>
              <Box>
                <Chip 
                    label="Secure Your Data" 
                    color="warning" 
                    sx={{ mr: 2, fontSize: '1rem', height: 40, px: 2 }} 
                    icon={<span role="img" aria-label="shield">🛡️</span>}
                />
                <Button variant="outlined" color="error" onClick={handleLockDatacenter}>
                    Lock Data Center
                </Button>
              </Box>
           </Box>

           <Grid container spacing={3}>
               <Grid size={{ xs: 12, md: 4 }}>
                   <Card variant="outlined" sx={{ height: '100%' }}>
                       <CardContent>
                           <Typography variant="h6" gutterBottom>Backup Database</Typography>
                           <Typography variant="body2" color="textSecondary" paragraph>
                               Select tables to backup:
                           </Typography>
                           <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel id="table-select-label">Tables</InputLabel>
                                <Select
                                labelId="table-select-label"
                                multiple
                                value={selectedTables}
                                onChange={handleTableChange}
                                input={<OutlinedInput label="Tables" />}
                                renderValue={(selected) => selected.length === tables.length ? 'All Tables' : selected.join(', ')}
                                MenuProps={MenuProps}
                                >
                                <MenuItem value="All Tables">
                                    <Checkbox checked={selectedTables.length === tables.length && tables.length > 0} />
                                    <ListItemText primary="All Tables" />
                                </MenuItem>
                                {tables.map((table) => (
                                    <MenuItem key={table.name} value={table.name}>
                                    <Checkbox checked={selectedTables.indexOf(table.name) > -1} />
                                    <ListItemText primary={table.name} />
                                    </MenuItem>
                                ))}
                                </Select>
                            </FormControl>
                           <Button variant="contained" color="primary" fullWidth onClick={handleBackup}>
                               Create & Download Backup
                           </Button>
                       </CardContent>
                   </Card>
               </Grid>

               <Grid size={{ xs: 12, md: 4 }}>
                   <Card variant="outlined" sx={{ height: '100%' }}>
                       <CardContent>
                           <Typography variant="h6" gutterBottom>Download ZIP Data</Typography>
                           <Typography variant="body2" color="textSecondary" paragraph>
                               Select tables and download data in ZIP format with CSV files.
                           </Typography>
                           <Box sx={{ height: 56, mb: 2 }} />
                           <Button variant="contained" color="info" fullWidth onClick={handleDownloadZipStep3}>
                               Download ZIP Data
                           </Button>
                       </CardContent>
                   </Card>
               </Grid>

               <Grid size={{ xs: 12, md: 4 }}>
                   <Card variant="outlined" sx={{ height: '100%' }}>
                       <CardContent>
                           <Typography variant="h6" gutterBottom>Restore Database</Typography>
                           <Typography variant="body2" color="textSecondary" paragraph>
                               Upload SQL backup file:
                           </Typography>
                           <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                                <Button variant="contained" component="label" size="small" sx={{ mr: 2 }}>
                                Choose File
                                <input type="file" hidden accept=".sql" onChange={(e) => setRestoreFile(e.target.files[0])} />
                                </Button>
                                <Typography variant="body2" noWrap>
                                    {restoreFile ? restoreFile.name : 'No file chosen'}
                                </Typography>
                           </Box>
                           <Button variant="contained" color="success" fullWidth onClick={handleRestore} disabled={!restoreFile}>
                               Restore Database
                           </Button>
                       </CardContent>
                   </Card>
               </Grid>
           </Grid>
        </Box>
      )}

      {step === 4 && renderStep4()}

      <Dialog 
        open={previewOpen} 
        onClose={handleClosePreview}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Preview: {getDisplayName(previewTable)}
          <Typography variant="caption" display="block" color="textSecondary">
            Total {totalRecords} records found
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {previewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : previewData.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>#</TableCell>
                    {Object.keys(previewData[0]).map((key) => (
                      <TableCell key={key} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', bgcolor: '#f5f5f5' }}>
                        {key.replace(/_/g, ' ').toUpperCase()}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.map((row, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {page * rowsPerPage + i + 1}
                      </TableCell>
                      {Object.keys(row).map((key, j) => (
                        <TableCell key={j} sx={{ whiteSpace: 'nowrap' }}>
                            {formatCellValue(key, row[key])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
             <Alert severity="info">No data available in this table.</Alert>
          )}
        </DialogContent>
        <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
        />
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Download OTP Dialog */}
       <Dialog open={downloadOtpOpen} onClose={handleCloseDownloadOtp} maxWidth="sm" fullWidth>
         <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
            Secure Download Verification
         </DialogTitle>
         <DialogContent>
            <Box sx={{ p: 2, textAlign: 'center' }}>
              {downloadOtpStep === 1 ? (
                <>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    To ensure security, please verify your identity before downloading {pendingDownloadType} files.
                    We will send a One-Time Password (OTP) to your registered email.
                  </Typography>
                  <Button 
                    variant="contained" 
                    size="large" 
                    onClick={handleSendDownloadOtp}
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    Please enter the OTP sent to your email address.
                  </Typography>
                  <TextField
                    fullWidth
                    label="Enter OTP"
                    variant="outlined"
                    value={downloadOtp}
                    onChange={(e) => setDownloadOtp(e.target.value)}
                    sx={{ mb: 3 }}
                  />
                  <Button 
                    variant="contained" 
                    color="success"
                    size="large" 
                    onClick={handleVerifyAndDownload}
                    disabled={loading || !downloadOtp}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Download'}
                  </Button>
                </>
              )}
            </Box>
         </DialogContent>
         <DialogActions>
           <Button onClick={handleCloseDownloadOtp} color="inherit">Cancel</Button>
         </DialogActions>
       </Dialog>

       {/* Download Link Modal */}
       <Dialog open={downloadLinkOpen} onClose={() => setDownloadLinkOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Choose Save Location</DialogTitle>
          <DialogContent>
            <Box sx={{ p: 2 }}>
               {/* Info Box */}
               <Paper variant="outlined" sx={{ p: 2, bgcolor: '#e3f2fd', borderColor: '#90caf9', mb: 3 }}>
                 <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                   <InfoIcon color="primary" sx={{ mr: 1, mt: 0.5 }} />
                   <Box>
                     <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                       How to Choose Save Location:
                     </Typography>
                     <Typography variant="body2" component="div">
                       <ol style={{ paddingLeft: '20px', margin: 0 }}>
                         <li><b>Right-click</b> the download button below</li>
                         <li>Select <b>"Save link as..."</b> or <b>"Save target as..."</b></li>
                         <li>Choose your desired folder and filename</li>
                         <li>Click <b>"Save"</b></li>
                       </ol>
                     </Typography>
                   </Box>
                 </Box>
               </Paper>
    
               {/* Download Button */}
               <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                 <Button 
                   variant="contained" 
                   color="primary" 
                   size="large"
                   href={downloadLinkUrl}
                   target="_blank"
                   startIcon={<DownloadIcon />}
                   sx={{ 
                     px: 4, 
                     py: 1.5, 
                     fontSize: '1.1rem', 
                     textTransform: 'none', 
                     borderRadius: 2 
                   }}
                 >
                   Download File
                 </Button>
               </Box>
               
               {/* Tip */}
               <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', justifyContent: 'center' }}>
                 <LightbulbIcon fontSize="small" sx={{ mr: 1, color: '#fbc02d' }} />
                 <Typography variant="caption" sx={{ fontSize: '0.85rem' }}>
                   Tip: Right-click the button above and select "Save link as..." to choose your save location
                 </Typography>
               </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDownloadLinkOpen(false)} variant="contained" color="secondary">Close</Button>
          </DialogActions>
       </Dialog>

       <Snackbar
        open={!!result}
        autoHideDuration={6000}
        onClose={() => setResult(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={result?.type} onClose={() => setResult(null)}>
          {result?.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}
